import React from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import googleIconUrl from '@/assets/images/Google.png';
import appleIconUrl from '@/assets/images/apple.avif';

export type ThirdPartyProvider = 'google' | 'apple' | 'email';
export type ThirdPartyAuthStatus = 'success' | 'error';

export interface ThirdPartyAuthToastState {
	status: ThirdPartyAuthStatus;
	provider?: ThirdPartyProvider;
	message?: string;
}

interface ThirdPartyAuthToastProps {
	open: boolean;
	toast: ThirdPartyAuthToastState | null;
	onClose: () => void;
}

interface ThirdPartyAuthContentProps {
	toast: ThirdPartyAuthToastState;
	onClose?: () => void;
}

export function ThirdPartyAuthContent({ toast, onClose }: ThirdPartyAuthContentProps) {
	const isSuccess = toast.status === 'success';
	const isEmail = toast.provider === 'email';
	const providerName = toast.provider === 'apple' ? 'Apple' : 'Google';
	const iconUrl = toast.provider === 'apple' ? appleIconUrl : googleIconUrl;

	return (
		<div className="relative w-full h-full flex flex-col items-center pt-10 pb-12 px-6">
			{onClose && (
				<button
					type="button"
					onClick={onClose}
					className="absolute right-5 top-5 h-10 w-10 flex items-center justify-center rounded-full bg-[#f7f7f8]/5 text-[#f7f7f8]/60 transition-colors hover:text-[#f7f7f8]"
					aria-label="Close"
				>
					<X className="h-5 w-5" strokeWidth={1.5} />
				</button>
			)}

			{!isEmail && (
				<div
					className={`flex h-14 w-14 items-center justify-center rounded-full border border-solid mb-8 ${
						isSuccess ? 'border-[#36e278] bg-[#1e2748]' : 'border-[#FF5A6A] bg-[#2A1420]'
					}`}
				>
					{isSuccess ? (
						<img src={iconUrl} alt={providerName} className="h-6 w-6 object-contain" />
					) : (
						<X className="h-6 w-6 text-[#FF5A6A]" strokeWidth={2.5} />
					)}
				</div>
			)}

			{isEmail && isSuccess && (
				<div className="flex h-14 w-14 items-center justify-center rounded-full border border-solid border-[#36e278] bg-[#1e2748] mb-6 mt-10">
					<CheckCircle className="h-6 w-6 text-[#BDF4C3]" strokeWidth={1.5} />
				</div>
			)}

			<div className="text-center mb-4">
				<h2 className="text-[20px] font-semibold leading-6 text-[#f7f7f8] tracking-[-0.1px]">
					{isSuccess ? (
						isEmail ? (
							"You're all set"
						) : (
							<>
								Successfully connected with
								<br />
								{providerName}
							</>
						)
					) : (
						'Login failed'
					)}
				</h2>
			</div>

			<p className="text-[14px] font-normal leading-normal text-[#d4d5d8] text-center max-w-[280px]">
				{isSuccess ? "You're good to go!" : toast.message}
			</p>
		</div>
	);
}

export function ThirdPartyAuthToast({ open, toast, onClose }: ThirdPartyAuthToastProps) {
	if (!toast) return null;

	return (
		<Drawer open={open} onOpenChange={(v) => !v && onClose()}>
			<DrawerContent
				hideHandle
				className="bg-[#151B2E] border-none rounded-t-[38px] h-auto min-h-[360px] p-0 font-['DM_Sans'] overflow-hidden z-[1000] lg:!bottom-auto lg:!inset-x-auto lg:top-1/2 lg:!left-1/2 lg:!right-auto lg:-ml-[196.5px] lg:-mt-[180px] lg:max-w-[393px] lg:w-full lg:rounded-[38px]"
			>
				<ThirdPartyAuthContent toast={toast} onClose={onClose} />
			</DrawerContent>
		</Drawer>
	);
}
